import { ROUTE } from '@/facade/enum.js';
import { websocketListen } from '@/hook/system/websocket.js';
import { Toast } from '@/hook/toast.js';
import { WEBSOCKET_CHANNEL } from '@common/schema/ws.js';
import { useCooldown } from 'bambam-helper/timed.js';
import { games, getBySlug, setGameBySlug, slug } from '@/hook/market/game.js';
import { filters, loadFilters, minimum_price, transformInfo } from '@/hook/market/items.js';
import {
  clearWithdrawItemProgress,
  clearWithdrawNot,
  deleteWithdrawItemProgress,
  setWithdrawItemProgress,
  WithdrawProgressEnum,
} from '@/hook/market/withdraw.js';
import { DEPOSIT_TYPE } from '@common/enum/market.js';
import { calculateItemFee } from '@common/fee.js';
import { API_ROUTE } from '@common/schema/api.js';
import { getOrderBySchema, makeParamsOptional, objectWrapper, SCHEMA_FIELD } from 'bambam-enum/validator.js';
import { BS_SCHEMA_FIELD } from '@common/schema/schema.js';
import { EventEmitter } from 'bambam-event';
import { useEndpointFacade, useFormFacade } from '@/facade/api.js';
import { replaceParams } from 'bambam-helper/router.js';
import { useStorage } from 'bambam-storage';
import { clone, deepMerge } from 'bambam-util/cherrypick.js';
import { _merge } from 'bambam-util-deprecated/cherrypick.js';
import { computed, reactive, ref, toRaw, watch } from 'vue';
import { router, session, translate } from '@/main.js';
import { status } from '@/hook/status.js';
import { isArray, isNumber } from 'bambam-util/is.js';
import { formatCurrency } from '@/hook/currency.js';

export const inventoryUpdates = new EventEmitter();

export const items_limit = ref(100);

export const handleLocalForm = (payload, items) => {
  let result = clone(items);
  const payload_order = [...payload.order || [{ field: 'id', order: 'DESC' }]];
  payload_order.unshift({
    field: 'skin_status',
    order: 'DESC',
  });
  if (payload_order) {
    const test = (a, b, field, order) => {
      if (a[field] === b[field]) return 0;
      const r = a[field] > b[field] ? 1 : -1;
      return order === 'ASC' ? r : -r;
    };
    result = result.sort((a, b) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const { field, order } of payload_order) {
        const r = test(a, b, field, order);
        if (r) return r;
      }
      return 0;
    });
  }
  if (!payload.where) {
    return result;
  }
  result = result.filter((item) => {
    if (payload.where.skin_name && !(((item.skin_name || item.name)?.toLowerCase() || '').includes(payload.where.skin_name.toLowerCase()))) {
      return false;
    }
    if (payload.where.price_from && !(item.suggested_price >= payload.where.price_from)) {
      return false;
    }
    if (payload.where.price_to && !(item.suggested_price <= payload.where.price_to)) {
      return false;
    }
    let tradehold;
    if (typeof item.tradehold !== 'undefined') {
      tradehold = item.tradehold;
    } else {
      tradehold = item.tradable ? 0 : 7;
    }
    if (!item.skin_status) {
      tradehold = 999;
    }
    if (typeof payload.where.tradehold_from !== 'undefined' && !(tradehold >= payload.where.tradehold_from)) {
      return false;
    }
    if (typeof payload.where.tradehold_to !== 'undefined' && !(tradehold <= payload.where.tradehold_to)) {
      return false;
    }
    if (typeof payload.where.sticker_counter_from !== 'undefined' && !(item.sticker_counter >= payload.where.sticker_counter_from)) {
      return false;
    }
    if (typeof payload.where.sticker_counter_to !== 'undefined' && !(item.sticker_counter <= payload.where.sticker_counter_to)) {
      return false;
    }
    if (payload.where.stickers) {
      if (!item.stickers) {
        return false;
      }
      if (payload.where.stickers[0].slot) {
        // eslint-disable-next-line no-restricted-syntax
        for (const sticker of payload.where.stickers) {
          if (!item.stickers.find((o) => o.slot === sticker.slot && o.name === sticker.name)) {
            return false;
          }
        }
      } else {
        const need_stickers = payload.where.stickers.reduce((o, a) => {
          if (o[a.name]) {
            o[a.name] += 1;
          } else {
            o[a.name] = 1;
          }
          return o;
        }, {});
        const have_stickers = item.stickers.reduce((o, a) => {
          if (o[a.name]) {
            o[a.name] += 1;
          } else {
            o[a.name] = 1;
          }
          return o;
        });
        // eslint-disable-next-line no-restricted-syntax
        for (const key in need_stickers) {
          if (!have_stickers[key] || have_stickers[key] < need_stickers[key]) {
            return false;
          }
        }
      }
    }
    const list_id = Object.keys(payload.where).filter((key) => key.endsWith('_id'));
    // eslint-disable-next-line no-restricted-syntax
    for (const key of list_id) {
      const value = payload.where[key];
      const valid = value instanceof Array ? value.includes(item[key]) : value === item[key];
      if (!valid) {
        return false;
      }
    }
    const list_checkbox = Object.keys(payload.where).filter((key) => key.startsWith('has_'));
    // eslint-disable-next-line no-restricted-syntax
    for (const key of list_checkbox) {
      const key_value = payload.where[key];
      const item_value = item[key.replace('has_', '')];
      if (key_value) {
        return !!item_value;
      }
    }
    return true;
  });
  return result;
};

export const withdraw_active = ref(false);

export const useInventory = (props) => {
  const form = ref(props.form);
  const app_id = ref(getBySlug(props.game));
  const { item: bigger, set: setBigger } = useStorage('inventory.bigger', false);
  const group = ref(false);

  const submit = (f) => (f.onSubmitInstant ? f.onSubmitInstant() : f.onSubmit());

  const toggleWithdraw = (v) => {
    if (v && props.type !== 'withdraw') {
      replaceParams(router, { type: 'withdraw' });
    }
    if (!v && props.type !== 'sell') {
      replaceParams(router, { type: 'sell' });
    }
    group.value = false;
    withdraw_active.value = v;
  };

  watch(() => props.type, (v) => {
    if (v) {
      toggleWithdraw(v === 'withdraw');
    }
  }, { immediate: true });

  const api_items_raw = ref(null);

  watch(() => form.value.api_response, (data) => {
    api_items_raw.value = data?.list || data;
  }, { immediate: true });

  const api_items = computed(() => {
    if (form.value.is_loading.value || !(api_items_raw.value instanceof Array)) {
      return [];
    }
    return api_items_raw.value.map((item) => transformInfo({
      ...item,
      app_id: app_id.value,
    }));
  });
  const api_items_total_value = computed(() => api_items.value.reduce((sum, item) => sum + item.suggested_price, 0));

  let main_form;
  let main_items;
  let counter;
  let initial_tradehold = true;
  if (props.local) {
    const forms = Object.fromEntries(games.map((game) => {
      const where_props = API_ROUTE.market.search[`mine_${game.app_id}`]?.schema?.where?.props;
      if (where_props.stickers) {
        where_props.stickers = _merge(where_props.stickers, {
          items: {
            props: {
              name: SCHEMA_FIELD.name,
            },
          },
        });
      }
      const LocalForm = useFormFacade({
        endpoint: {
          path: '/local/inventory',
          method: null,
          schema: makeParamsOptional({
            order: getOrderBySchema(['suggested_price', 'asset_id'], true),
            where: objectWrapper(makeParamsOptional({
              ...where_props,
            })),
          }),
        },
        submitAction: ({ payload }) => {
          LocalForm.items.value = handleLocalForm(payload, api_items.value);
        },
      });
      LocalForm.items = ref();
      LocalForm.onSubmitInstant = LocalForm.onSubmit;
      LocalForm.is_local = true;

      websocketListen(WEBSOCKET_CHANNEL.item.extra_info, (item) => {
        if (item.own) {
          if (!api_items_raw.value) {
            return;
          }
          api_items_raw.value = api_items_raw.value.map((o) => {
            if (item.asset_id === o.asset_id) {
              return { ...o, ...item };
            }
            return o;
          });
        }
      });

      return [game.app_id, LocalForm];
    }));

    const LocalForm = computed(() => forms[app_id.value]);

    watch(api_items, () => {
      if (props.deposit && initial_tradehold) {
        LocalForm.value.setValue('where.tradehold_to', 0);
        initial_tradehold = false;
      }
      submit(LocalForm.value);
    }, { immediate: true });

    main_form = LocalForm;
    main_items = computed(() => LocalForm.value.items.value || []);
    counter = computed(() => null);
  } else {
    main_form = computed(() => toRaw(form.value));
    main_items = api_items;
    counter = computed(() => form.value.api_response?.counter?.filtered || 0);
  }

  const extras = reactive(new Map());
  const selected = reactive(new Map());
  const withdrawal = reactive(new Map());
  const delisting = reactive(new Set());
  const items_cache = reactive(new Map());

  const clear = () => {
    selected.clear();
    extras.clear();
    withdrawal.clear();
    delisting.clear();
    items_cache.clear();
    clearWithdrawItemProgress();
  };

  const refresh = () => {
    clear();
    submit(form.value);
  };

  const api_items_selected = computed(() => Array.from(items_cache.values()).filter((o) => selected.has(o[props.field])));

  const api_items_withdrawal = computed(() => Array.from(items_cache.values()).filter((o) => withdrawal.has(o[props.field])));

  const updateSelected = (id, value, item) => {
    if (!id) return;
    if (item) {
      items_cache.set(id, item);
    }
    selected.set(id, value);
    delisting.delete(id);
    // withdrawal.delete(id);
    // deleteWithdrawItemProgress(id);
  };

  const updateWithdrawal = (id, value, item) => {
    if (!id) return;
    if (item) {
      items_cache.set(id, item);
    }
    withdrawal.set(id, value);
    delisting.delete(id);
    // selected.delete(id);
    extras.delete(id);
    setWithdrawItemProgress(id, WithdrawProgressEnum.selected, item);
  };

  const isDisabled = (item, type = props.type) => Boolean((!props.bot && (item.locked || item.skin_status !== 1)) || (type === 'withdraw' && item.tradehold));

  const setSelected = (item, v) => {
    if (isDisabled(item, 'sale')) {
      return;
    }
    const id = item[props.field];
    if (v && props.selectItemsLimit && props.selectItemsLimit < selected.size && !selected.has(id)) {
      Toast.toast({
        type: 'error',
        text: translate('market.max_items_selected', { limit: props.selectItemsLimit }),
        counter: 1,
      });
      return;
    }
    let price;
    if (typeof v === 'number') {
      price = v;
    } else {
      price = v ? (item.price || item.suggested_price || minimum_price) : false;
    }

    if (v) {
      updateSelected(id, price, item);
      const already = extras.get(id);
      extras.set(id, { is_private: false, ...already });
    } else {
      selected.delete(id);
      delisting.delete(id);
      extras.delete(id);
      // @TODO: ok czy clearWithdrawNot + statusy
      withdrawal.delete(id);
    }
  };

  const clearWithdrawalNotSelected = () => {
    const cleared = clearWithdrawNot(WithdrawProgressEnum.selected);
    cleared.forEach((key) => {
      withdrawal.delete(key);
    });
  };

  const setWithdraw = (item, v) => {
    if (!item || item.tradehold) {
      return;
    }
    const { id } = item;
    if (v && props.withdrawItemsLimit && props.withdrawItemsLimit < withdrawal.size && !withdrawal.has(id)) {
      Toast.toast({
        type: 'error',
        text: translate('market.max_items_selected', { limit: props.withdrawItemsLimit }),
        counter: 1,
      });
      return;
    }
    clearWithdrawalNotSelected();
    if (v) {
      updateWithdrawal(id, item.price || item.suggested_price || 0, item);
      setWithdrawItemProgress(id, WithdrawProgressEnum.selected, item);
    } else {
      withdrawal.delete(id);
      selected.delete(id);
      deleteWithdrawItemProgress(id);
    }
  };

  const api_selected_total_value = computed(() => Array.from(selected.values()).reduce((sum, price) => sum + (price || 0), 0));

  const api_withdrawal_total_value = computed(() => Array.from(withdrawal.values()).reduce((sum, price) => sum + (price || 0), 0));

  const items_selected = ref([]);

  const SelectedLocalForm = useFormFacade({
    endpoint: {
      path: '/local/selected',
      method: null,
      schema: makeParamsOptional({
        order: getOrderBySchema(['suggested_price', 'asset_id'], true),
        where: objectWrapper(makeParamsOptional({
          skin_name: BS_SCHEMA_FIELD.skin_name,
        })),
      }),
    },
    submitAction: ({ payload }) => {
      items_selected.value = handleLocalForm(payload, api_items_selected.value);
    },
  });

  watch(selected, () => {
    submit(SelectedLocalForm);
  }, { immediate: true });

  const items_withdrawal = ref([]);

  const WithdrawalLocalForm = useFormFacade({
    endpoint: {
      path: '/local/withdrawal',
      method: null,
      schema: makeParamsOptional({
        order: getOrderBySchema(['suggested_price', 'id'], true),
        where: objectWrapper(makeParamsOptional({
          skin_name: BS_SCHEMA_FIELD.skin_name,
        })),
      }),
    },
    submitAction: ({ payload }) => {
      items_withdrawal.value = handleLocalForm(payload, api_items_withdrawal.value);
    },
  });

  watch(withdrawal, () => {
    submit(WithdrawalLocalForm);
  }, { immediate: true });

  const removeAllSelected = () => {
    api_items.value.forEach((item) => {
      setSelected(item, false);
    });
  };

  const removeAllWithdraw = () => {
    api_items.value.forEach((item) => {
      setWithdraw(item, false);
    });
  };

  const available_counter = computed(() => main_items.value.filter(i => i.tradable !== 0).length);

  const is_all_selected = computed(() => available_counter.value > 0 && available_counter.value === selected.size);

  const is_all_withdraw = computed(() => main_items.value.every((item) => withdrawal.get(item[props.field])));

  const toggleGroup = () => {
    group.value = !group.value;
  };

  watch(() => [props.game, props.gameType], () => {
    app_id.value = getBySlug(props.game);
    selected.clear();
    withdrawal.clear();
    extras.clear();
    props.gameChange(app_id.value);
    setGameBySlug(props.game);
    loadFilters(app_id.value);
    clearWithdrawItemProgress();
  }, { immediate: true });

  const items_group_selected = computed(() => {
    const result = {};
    items_selected.value.forEach((item) => {
      if (!result[item.name]) {
        result[item.name] = {
          ...item,
          quantity: api_items_selected.value.filter((o) => o.name === item.name).length,
          quantity_max: api_items.value.filter((o) => o.name === item.name).length,
          same_price: api_items_selected.value
            .filter((o) => o.name === item.name)
            .every((o) => selected.get(o[props.field]) === selected.get(item[props.field])),
          any_delisting: api_items_selected.value
            .filter((o) => o.name === item.name)
            .some((o) => delisting.has(o[props.field])),
          same_delisting: api_items_selected.value
            .filter((o) => o.name === item.name)
            .every((o) => delisting.has(o[props.field])),
        };
      }
    });
    return result;
  });

  const updateGroup = (item, { quantity, price, is_private }) => {
    const item_group = items_group_selected.value[item.name];
    api_items_selected.value.filter((o) => o.name === item.name).forEach((o) => {
      updateSelected(o[props.field], price, o);
      extras.set(o[props.field], { is_private });
    });
    if (quantity > item_group.quantity) {
      const to_add = quantity - item_group.quantity;
      api_items.value
        .filter((o) => o.name === item.name && !selected.has(o[props.field]))
        .slice(0, to_add)
        .forEach((o) => {
          updateSelected(o[props.field], price, o);
          extras.set(o[props.field], { is_private });
        });
    } else if (quantity < item_group.quantity) {
      api_items_selected.value
        .filter((o) => o.name === item.name)
        .slice(quantity)
        .forEach((o) => {
          selected.delete(o[props.field]);
          delisting.delete(o[props.field]);
          extras.delete(o[props.field]);
        });
    }
  };

  const handleDelistGroup = (item) => {
    let delist = !item.any_delisting;
    if (!delist && !item.same_delisting) {
      delist = true;
    }

    api_items_selected.value
      .filter((o) => o.name === item.name)
      .forEach((o) => {
        if (delist) {
          delisting.add(o[props.field]);
        } else {
          delisting.delete(o[props.field]);
        }
      });
  };

  const earnings = computed(() => Array.from(selected.values()).reduce((o, a) => o + a, 0));

  const updateExtras = (item, data) => {
    extras.set(item.asset_id, data);
  };

  const toggleSelected = (item, single) => {
    if (single) {
      setSelected(item, !selected.has(item[props.field]));
    } else {
      setWithdraw(item, !withdrawal.has(item[props.field]));
      setSelected(item, !selected.has(item[props.field]));
    }
  };

  const removeRawItem = (id, field = 'id') => {
    if (api_items_raw.value) {
      api_items_raw.value = api_items_raw.value.filter((o) => String(o[field]) !== String(id));
    }
  };

  const removeItem = (id, field = 'id') => {
    selected.delete(String(id));
    withdrawal.delete(String(id));
    delisting.delete(String(id));
    removeRawItem(id, field);
  };

  const updateItem = (item, field = 'id') => {
    if (api_items_raw.value) {
      api_items_raw.value = api_items_raw.value.map((o) => {
        if (String(o[field]) === String(item[field])) {
          return {
            ...o,
            ...item,
            id: o.id,
            asset_id: o.asset_id,
          };
        }
        return o;
      });
    }
  };

  const handleDelist = (item) => {
    if (delisting.has(item[props.field])) {
      delisting.delete(item[props.field]);
    } else {
      delisting.add(item[props.field]);
    }
  };

  const setPage = (value) => {
    main_form.value.setValue('offset', value);
    submit(main_form.value);
  };

  inventoryUpdates.on('deposit_group', ({ result }) => {
    result?.available?.forEach((id) => {
      removeItem(String(id), 'asset_id');
    });
    result?.missing?.forEach((id) => {
      removeItem(String(id), 'asset_id');
    });
    result?.not_accepting?.forEach((id) => {
      selected.delete(String(id));
    });
  });

  inventoryUpdates.on('relist', ({ payload }) => {
    if (!payload?.id) {
      return;
    }
    removeItem(Number(payload.id));
  });

  inventoryUpdates.on('withdraw', ({ payload, result }) => {
    if (!payload?.id) {
      return;
    }
    if (result) {
      selected.delete(payload.id);
      removeRawItem(Number(payload.id));
    }
  });

  inventoryUpdates.on('update', ({ payload }) => {
    if (!payload?.id) {
      return;
    }
    withdrawal.delete(payload.id);
    selected.delete(payload.id);
    updateItem(payload);
  });

  inventoryUpdates.on('delist', ({ payload }) => {
    if (!payload?.id) {
      return;
    }
    selected.delete(payload.id);
    removeItem(Number(payload.id));
  });

  const { action: retry, disabled: retry_disabled } = useCooldown(() => submit(form.value), 3e3);

  const main_filters = computed(() => ({
    ...filters[app_id.value],
    ...Object.fromEntries(props.removeFilters.map((key) => [key, false])),
  }));

  const updateForm = (v) => {
    form.value = v;
  };

  const amount_add_to_sale = computed(() => {
    const amount = main_items.value.filter((item) => !selected.has(item[props.field]) && !isDisabled(item, 'sale')).length;
    const already = selected.size;
    return Math.max(0, (props.selectItemsLimit ? Math.min(amount, props.selectItemsLimit - already) : amount));
  });

  const amount_add_to_withdraw = computed(() => {
    const amount = main_items.value.filter((item) => !withdrawal.has(item[props.field]) && !isDisabled(item, 'withdraw')).length;
    const already = withdrawal.size;
    return Math.max(0, (props.withdrawItemsLimit ? Math.min(amount, props.withdrawItemsLimit - already) : amount));
  });

  const selectAllVisible = () => {
    if (props.withdraw) {
      toggleWithdraw(false);
    }
    main_items.value
      .filter((o) => !isDisabled(o, 'sale') && !selected.has(o[props.field]))
      .slice(0, amount_add_to_sale.value)
      .forEach((item) => {
        setSelected(item, true);
      });
  };

  const deselectAllVisible = () => {
    if (props.withdraw) {
      toggleWithdraw(false);
    }
    main_items.value
      .forEach((item) => {
        setSelected(item, false);
      });
  };

  const delistAllVisible = (v) => {
    main_items.value.forEach((item) => {
      setSelected(item, v);
      if (v) {
        delisting.add(item[props.field]);
      } else {
        delisting.delete(item[props.field]);
      }
    });
    toggleWithdraw(false);
  };

  const withdrawAllVisible = () => {
    toggleWithdraw(true);
    main_items.value
      .filter((o) => !isDisabled(o, 'withdraw') && !withdrawal.has(o[props.field]))
      .slice(0, amount_add_to_withdraw.value)
      .forEach((item) => {
        setWithdraw(item, true);
      });
  };

  watch(() => session.profile.value.steam_token, (current, old) => {
    if (!old && current) {
      retry();
    }
  });

  const is_tradable = computed(() => typeof main_form.value.getValue('where.tradehold_to') !== 'undefined');
  const toggleTradable = () => {
    if (!is_tradable.value) {
      main_form.value.setValue('where.tradehold_to', 0);
    } else {
      main_form.value.clearValue('where.tradehold_to');
    }
    submit(main_form.value);
  };

  return {
    items_cache,
    api_items,
    api_items_selected,
    api_items_total_value,
    api_items_withdrawal,
    api_selected_total_value,
    api_withdrawal_total_value,
    bigger,
    counter,
    delisting,
    earnings,
    extras,
    group,
    handleDelist,
    handleDelistGroup,
    isDisabled,
    items_group_selected,
    items_selected,
    items_withdrawal,
    SelectedLocalForm,
    WithdrawalLocalForm,
    main_form,
    main_items,
    refresh,
    clear,
    removeAllSelected,
    removeAllWithdraw,
    selectAllVisible,
    deselectAllVisible,
    delistAllVisible,
    selected,
    setBigger,
    setPage,
    setSelected,
    setWithdraw,
    toggleGroup,
    toggleSelected,
    updateExtras,
    updateGroup,
    withdraw_active,
    withdrawal,
    retry,
    retry_disabled,
    main_filters,
    updateForm,
    submit,
    is_all_selected,
    available_counter,
    withdrawAllVisible,
    is_all_withdraw,
    amount_add_to_sale,
    amount_add_to_withdraw,
    is_tradable,
    toggleTradable,
  };
};

export const calculateTotalFee = (items, deposit_type, selected, field) => items.reduce((o, item) => o + (calculateItemFee({
  price: Math.max(selected.get(item[field]) || 0, 0),
  is_private_sell: deposit_type === DEPOSIT_TYPE.private,
}, session.profile.value)?.fee_amount || 0), 0);

export const inventory_nav_list = computed(() => [
  {
    name: 'market.steam_inventory',
    route: { name: ROUTE.steam_inventory_game },
  },
  {
    name: 'market.bitskins_inventory',
    route: { name: ROUTE.bs_inventory_game, params: { type: 'sell' } },
  },
  {
    name: 'account.selling',
    route: { name: ROUTE.selling_inventory_game, params: { type: 'public' } },
  },
  {
    name: 'market.queued',
    route: { name: ROUTE.queued_game },
  },
  {
    name: 'market.withdrawn',
    route: { name: ROUTE.withdrawn_game },
  },
  // {
  //   name: 'market.issues',
  //   route: { name: ROUTE.issues_game },
  // },
  status.buy_order ? {
    name: 'market.buy_orders',
    route: { name: ROUTE.buy_orders_create },
    meta_name: 'buy_orders',
  } : null,
  {
    name: 'market.favorites',
    route: { name: ROUTE.favorites_game },
  },
].filter((v) => v).map((o) => deepMerge(o, { route: { params: { game: slug.value } } })));

export const getUpdateData = (payload, result) => ({
  payload: payload.items.find((o) => String(o.id) === String(result.id)),
  result: result.success,
});

export const getInventoryStats = (inventory_status = null) => {
  const inventory_stats = ref([]);

  const GetInventoryStats = useEndpointFacade({
    endpoint: API_ROUTE.market.stats.get,
    transformData: (payload) => ({
      app_id: payload,
    }),
    transformResult: (el) => {
      if (isArray(inventory_status)) {
        return el.filter((o) => inventory_status.includes(o.status));
      }
      if (isNumber(inventory_status)) {
        return el.filter((o) => o.status === inventory_status);
      }
      return el;
    },
    onSuccess: (data) => {
      inventory_stats.value = data;
    },
  });

  const sum_item_inventory = computed(() => inventory_stats.value.reduce((acc, curr) => acc + (curr?.counter || 0), 0));
  const sum_inventory_stats = computed(() => formatCurrency(inventory_stats.value.reduce((acc, curr) => acc + (Number(curr?.total) || 0), 0)));

  const sort = (status) => {
    let el = [];
    if (isArray(status)) {
      el = inventory_stats.value.filter((o) => status.includes(o));
    }
    if (isNumber(status)) {
      el = inventory_stats.value.filter((o) => o.status === status);
    }
    return el;
  };

  const sum_by_status = (status) => formatCurrency(sort(status).reduce((acc, curr) => acc + (curr?.total || 0), 0));
  const sum_item_by_status = (status) => sort(status).reduce((acc, curr) => acc + (curr?.counter || 0), 0);

  return {
    checkInventoryStats: (app_id) => GetInventoryStats.call(app_id),
    inventory_stats,
    sum_inventory_stats,
    sum_item_inventory,
    sum_item_by_status,
    sum_by_status,
  };
};
